import { Icon } from '@eti/components';
import type { SpacingTypes } from '@etg/wings';
import { Inline } from '@etg/wings';
import { css } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import type { BaggageInfo } from './TripBaggageInfo';

const baggageNumberWrapperStyles = css`
  font-size: 0.875rem;
  font-weight: 700;
  gap: 2px;
`;

const BaggageType = {
  PERSONAL_ITEM: 'personalItem',
  CABIN_BAGGAGE: 'cabinBaggage',
  CHECKED_BAGGAGE: 'checkedBaggage',
} as const;

interface BaggageListProps {
  baggageInfo: BaggageInfo;
  spacing?: SpacingTypes;
}

export const BaggageList = ({ baggageInfo, spacing = 4 }: BaggageListProps) => {
  const { t } = useTranslation();
  const { includedCabinBaggage, includedCheckedBaggage } = baggageInfo;
  const isPersonalItemIncluded = includedCabinBaggage?.includedPersonalItem;
  const includedCabinBaggagePieces = includedCabinBaggage.pieces;

  const baggageList = [
    BaggageType.PERSONAL_ITEM,
    BaggageType.CABIN_BAGGAGE,
    BaggageType.CHECKED_BAGGAGE,
  ];
  const INCLUDED = 1;
  const NOT_INCLUDED = 0;

  const getCheckedBaggageQuantity = () => {
    const checkedBaggageWeightMeansOnePiece = includedCheckedBaggage?.weight
      ? INCLUDED
      : NOT_INCLUDED;

    return includedCheckedBaggage?.pieces
      ? includedCheckedBaggage.pieces
      : checkedBaggageWeightMeansOnePiece;
  };

  const personalItemPiece = isPersonalItemIncluded ? INCLUDED : NOT_INCLUDED;

  const baggagesQuantities = {
    personalItemQuantity: personalItemPiece,
    cabingBagQuantity: includedCabinBaggagePieces,
    checkedBaggageQuantity: getCheckedBaggageQuantity(),
  };

  const getBaggageIconAndStatus = (baggageType: string) => {
    switch (baggageType) {
      case BaggageType.PERSONAL_ITEM:
        return { baggageIcon: 'personal-item', pieces: personalItemPiece };
      case BaggageType.CABIN_BAGGAGE:
        return { baggageIcon: 'cabin-baggage', pieces: includedCabinBaggagePieces };
      case BaggageType.CHECKED_BAGGAGE:
        return {
          baggageIcon: 'check-in-baggage',
          pieces: getCheckedBaggageQuantity(),
        };
      default:
        return { baggageIcon: '', pieces: '' };
    }
  };

  return (
    <Inline
      aria-label={t(
        'ScreenReader.ResultCard.TripBaggageInfo.Button.Text',
        baggagesQuantities.personalItemQuantity,
        baggagesQuantities.cabingBagQuantity,
        baggagesQuantities.checkedBaggageQuantity,
      )}
      spacing={spacing}
    >
      {baggageList?.map((baggageType) => {
        const { pieces, baggageIcon } = getBaggageIconAndStatus(baggageType);
        return (
          <Inline
            className={baggageNumberWrapperStyles}
            data-testid={baggageType}
            key={baggageType}
          >
            {pieces}
            <Icon id={baggageIcon} size="20px" />
          </Inline>
        );
      })}
    </Inline>
  );
};
